'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { message } from 'antd';
import {
  getUserToken,
  setUserToken,
  clearUserToken,
  fetchUserProfile,
  fetchUserProfileResult,
  beginUserOAuth,
  getPendingReview,
  clearPendingReview,
  getPendingPhotos,
  clearPendingPhotos,
  submitReview,
  type UserAuthProvider as AuthProvider,
} from '../utils/reviewerApi';
import type { ReviewerProfile } from '../types';

// ── Types ────────────────────────────────────────────────────

interface PendingReviewData {
  entityId: number;
  entityName: string;
  formData: Record<string, unknown>;
}

interface PendingPhotosData {
  entityId: number;
  captions: string[];
}

interface UserAuthContextValue {
  /** The currently authenticated reviewer, or null */
  reviewer: ReviewerProfile | null;
  /** Whether the initial auth check is still running */
  isLoading: boolean;
  /** Shorthand for !!reviewer */
  isLoggedIn: boolean;
  /** Start OAuth flow for a provider */
  login: (provider: AuthProvider) => Promise<void>;
  /** Clear token and reset state */
  logout: () => void;
  /** Re-fetch the reviewer profile (e.g. after updating) */
  refreshProfile: () => Promise<void>;
  /** If a review was saved before OAuth redirect, it lives here */
  pendingReview: PendingReviewData | null;
  /** If community photos were queued before OAuth redirect */
  pendingPhotos: PendingPhotosData | null;
  /** Clear the pending review (after successful submission) */
  clearPending: () => void;
  /** Clear the pending photos (after successful upload) */
  clearPendingPhotosData: () => void;
  /** Auto-submit any pending review after OAuth callback */
  processPendingReview: () => Promise<boolean>;
}

const UserAuthContext = createContext<UserAuthContextValue | undefined>(
  undefined,
);

// ── Provider ─────────────────────────────────────────────────

export function UserAuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [reviewer, setReviewer] = useState<ReviewerProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingReview, setPendingReview] = useState<PendingReviewData | null>(
    null,
  );
  const [pendingPhotos, setPendingPhotos] = useState<PendingPhotosData | null>(
    null,
  );
  // Guards the post-login auto-submit against firing twice (re-render / React
  // Strict Mode) and double-posting the same review.
  const submittingRef = useRef(false);

  // ── Check for token in URL (OAuth callback) and validate ──

  const validateToken = useCallback(async () => {
    setIsLoading(true);
    try {
      // 1. Check if the OAuth callback just returned a token in the URL
      if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        const tokenFromUrl = params.get('reviewer_token');
        if (tokenFromUrl) {
          setUserToken(tokenFromUrl);
          // Clean the token from the URL to avoid leaking it in referrer etc.
          const cleanUrl = new URL(window.location.href);
          cleanUrl.searchParams.delete('reviewer_token');
          window.history.replaceState({}, '', cleanUrl.toString());
        }
      }

      // 2. If we have a token (either from URL or localStorage), validate it.
      const token = getUserToken();
      if (!token) {
        setReviewer(null);
        return;
      }

      // Only a definitive 401 means the token is bad — clear it and log out.
      // A 5xx or network error is transient (e.g. a Neo4j "defunct connection"
      // blip): retry a few times, and if it never clears, KEEP the token rather
      // than logging the user out. Previously any failure — including a
      // momentary DB hiccup on /user/me right after OAuth — cleared the token,
      // which is what dumped signed-in users out of the recommend-a-campsite
      // flow and lost their submission.
      let lastStatus = -1;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const { status, profile } = await fetchUserProfileResult();
          lastStatus = status;
          const p = profile as ReviewerProfile & { reviewer?: ReviewerProfile; id?: unknown } | null;
          if (p && (p.reviewer || p.id)) {
            setReviewer((p.reviewer ?? p) as ReviewerProfile);
            return;
          }
          if (status === 401) {
            clearUserToken();
            setReviewer(null);
            return;
          }
          // 200-but-empty or 5xx — fall through to retry.
        } catch (err) {
          // Network error (fetch rejected) — transient; retry.
          console.warn('[UserAuth] profile validation network error, will retry', err);
        }
        if (attempt < 2) {
          await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
        }
      }
      // Exhausted retries on a transient failure — keep the token so the user
      // stays signed in; their next authenticated action re-validates.
      console.warn(`[UserAuth] keeping token after transient profile-validation failures (last status ${lastStatus})`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ── Load pending submissions from sessionStorage ──────────

  const loadPending = useCallback(() => {
    const review = getPendingReview();
    const photos = getPendingPhotos();
    if (review) setPendingReview(review);
    if (photos) setPendingPhotos(photos);
  }, []);

  // ── On mount: validate + load pending ─────────────────────

  useEffect(() => {
    validateToken();
    loadPending();
  }, [validateToken, loadPending]);

  // ── Auto-submit pending review after successful login ─────

  const processPendingReview = useCallback(async (): Promise<boolean> => {
    const pending = getPendingReview();
    if (!pending || !reviewer) return false;
    if (submittingRef.current) return false;
    submittingRef.current = true;

    // The OAuth sign-in redirect closes the review drawer and usually lands the
    // user on a different page. Without visible feedback, a submit that
    // actually SUCCEEDED looked like the whole review had been thrown away —
    // the #1 complaint about the review flow. Make the outcome loud, wherever
    // they land (antd's global message renders app-wide).
    const key = 'pending-review';
    message.loading({ content: 'Posting your review…', key, duration: 0 });
    try {
      await submitReview(pending.entityId, pending.formData);
      clearPendingReview();
      setPendingReview(null);
      message.success({
        content: `Thanks! Your review of ${pending.entityName} is pending moderation and will appear shortly.`,
        key,
        duration: 6,
      });
      // Nudge the campsite page's reviews list to refresh if the user is on it.
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('review-submitted', {
            detail: { entityId: pending.entityId },
          }),
        );
      }
      return true;
    } catch (err) {
      console.error('[UserAuth] Auto-submit pending review failed:', err);
      // Keep the draft in sessionStorage so nothing is lost — reopening the
      // review form re-hydrates from it so the user can retry without retyping.
      message.error({
        content: `We saved your review of ${pending.entityName} but couldn't post it just yet. Open “Write a review” to try again — your text is still here.`,
        key,
        duration: 8,
      });
      return false;
    } finally {
      submittingRef.current = false;
    }
  }, [reviewer]);

  // After login completes and we have a reviewer, try to submit pending
  useEffect(() => {
    if (reviewer && pendingReview) {
      // Small delay to let UI settle
      const timer = setTimeout(() => {
        processPendingReview();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [reviewer, pendingReview, processPendingReview]);

  // ── Public API ────────────────────────────────────────────

  const login = useCallback(async (provider: AuthProvider) => {
    await beginUserOAuth(provider);
  }, []);

  const logout = useCallback(() => {
    clearUserToken();
    setReviewer(null);
    setPendingReview(null);
    setPendingPhotos(null);
  }, []);

  const refreshProfile = useCallback(async () => {
    const token = getUserToken();
    if (!token) return;
    try {
      const profile = await fetchUserProfile();
      if (profile && profile.reviewer) {
        setReviewer(profile.reviewer);
      } else if (profile && profile.id) {
        setReviewer(profile as unknown as ReviewerProfile);
      }
    } catch (err) {
      console.error('[UserAuth] Refresh profile failed:', err);
    }
  }, []);

  const clearPending = useCallback(() => {
    clearPendingReview();
    setPendingReview(null);
  }, []);

  const clearPendingPhotosData = useCallback(() => {
    clearPendingPhotos();
    setPendingPhotos(null);
  }, []);

  // ── Memoised context value ────────────────────────────────

  const value = useMemo<UserAuthContextValue>(
    () => ({
      reviewer,
      isLoading,
      isLoggedIn: !!reviewer,
      login,
      logout,
      refreshProfile,
      pendingReview,
      pendingPhotos,
      clearPending,
      clearPendingPhotosData,
      processPendingReview,
    }),
    [
      reviewer,
      isLoading,
      login,
      logout,
      refreshProfile,
      pendingReview,
      pendingPhotos,
      clearPending,
      clearPendingPhotosData,
      processPendingReview,
    ],
  );

  return (
    <UserAuthContext.Provider value={value}>
      {children}
    </UserAuthContext.Provider>
  );
}

// ── Hook ─────────────────────────────────────────────────────

export function useUserAuth(): UserAuthContextValue {
  const ctx = useContext(UserAuthContext);
  if (!ctx) {
    throw new Error(
      'useUserAuth must be used within a <UserAuthProvider>',
    );
  }
  return ctx;
}

// ── Backward-compatibility aliases ───────────────────────────

/** @deprecated Use UserAuthProvider */
export const ReviewerAuthProvider = UserAuthProvider;
/** @deprecated Use UserAuthContextValue */
export type ReviewerAuthContextValue = UserAuthContextValue;
/** @deprecated Use useUserAuth */
export const useReviewerAuth = useUserAuth;

export default UserAuthContext;
